import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, X, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const ECUploadPage = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (newFiles) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    
    const validatedFiles = Array.from(newFiles).filter(file => {
      if (!validTypes.includes(file.type)) {
        setError(`File ${file.name} has invalid type. Please upload PDF, DOCX, JPG, or PNG files`);
        return false;
      }
      
      if (file.size > 200 * 1024 * 1024) { // 200MB limit
        setError(`File ${file.name} is too large. Maximum size is 200MB`);
        return false;
      }
      
      return true;
    });
    
    if (validatedFiles.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...validatedFiles]);
      setError(null);
    }
  };

  const handleRemoveFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setError(null);
  };

  const handleExtract = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    setError(null);
    
    try {
      // Step 1: Upload all files
      const fileIds = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/ec/upload`,
          formData,
          {
            headers: {
            }
          }
        );
        
        if (!uploadResponse.data.success) {
          throw new Error(`File upload failed for ${file.name}`);
        }
        
        fileIds.push(uploadResponse.data.file_id);
      }
      
      setUploading(false);
      setExtracting(true);
      
      // Step 2: Extract EC data from all files
      const extractFormData = new FormData();
      fileIds.forEach(id => extractFormData.append('file_ids', id));
      
      const extractResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ec/extract`,
        extractFormData
      );
      
      if (!extractResponse.data.success) {
        throw new Error('EC extraction failed');
      }
      
      const ecId = extractResponse.data.ec_id;
      const extractedData = extractResponse.data.extracted_data;
      
      // Navigate to survey selection page
      navigate(`/ec/select-survey/${ecId}`, {
        state: {
          ecId,
          extractedData
        }
      });
      
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred');
      setUploading(false);
      setExtracting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Back to Dashboard</span>
              </button>
            </div>
            
            <div>
              <h1 className="text-xl font-bold text-gray-900">EC Extractor</h1>
              <p className="text-sm text-gray-500">Upload Encumbrance Certificate</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload EC Document</h2>
          <p className="text-gray-600 mb-8">Upload an Encumbrance Certificate to extract structured data automatically.</p>
          
          {/* Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 transition-colors ${
              dragActive 
                ? 'border-green-500 bg-green-50' 
                : files.length > 0
                ? 'border-green-300 bg-green-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {files.length === 0 ? (
              <div className="text-center">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Drag & drop your EC documents here
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  or click to browse (multiple files supported)
                </p>
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept=".pdf,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileInput}
                  multiple
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer transition-colors"
                >
                  <Upload className="w-5 h-5" />
                  <span>Choose Files</span>
                </label>
                <p className="text-xs text-gray-500 mt-4">
                  Supports PDF, DOCX, JPG, PNG (Max 200MB per file)
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-medium text-gray-900">{files.length} file(s) selected</p>
                  <input
                    type="file"
                    id="file-upload-more"
                    className="hidden"
                    accept=".pdf,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileInput}
                    multiple
                  />
                  <label
                    htmlFor="file-upload-more"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer transition-colors text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Add More</span>
                  </label>
                </div>
                
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 p-2 rounded">
                        <FileText className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      disabled={uploading || extracting}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Extract Button */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={uploading || extracting}
            >
              Cancel
            </button>
            <button
              onClick={handleExtract}
              disabled={files.length === 0 || uploading || extracting}
              className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Uploading {files.length} file(s)...</span>
                </>
              ) : extracting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Extracting Data...</span>
                </>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5" />
                  <span>Extract from {files.length} file(s)</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-3">What data will be extracted?</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• EC Metadata (application number, statement number, dates, search period)</li>
            <li>• Property Details (district, SRO, survey number, extent, boundaries)</li>
            <li>• Owner Information (name, father/husband name, address)</li>
            <li>• Complete Transaction History (all deeds, dates, parties, values)</li>
            <li>• Encumbrance Summary (mortgage status, remarks)</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default ECUploadPage;
